import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import StatusBadge from '../shared/StatusBadge';
import ShareConventionLink from './ShareConventionLink';

const fmt = d => new Date(d).toLocaleDateString('fr-FR');

export default function ConventionList({ onNew }) {
  const [conventions, setConventions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingShares, setPendingShares] = useState({}); // periodeId -> conventionId
  const [sharingLoading, setSharingLoading] = useState({}); // periodeId -> bool
  const navigate = useNavigate();

  const fetchConventions = () =>
    Promise.all([
      api.get('/conventions/mine'),
      api.get('/classes'),
    ]).then(([cv, cl]) => {
      setConventions(cv.data);
      setClasses(cl.data);
    }).catch(console.error);

  useEffect(() => {
    fetchConventions().finally(() => setLoading(false));
  }, []);

  const handleShare = async (classeId, periode) => {
    setSharingLoading(s => ({ ...s, [periode.id]: true }));
    try {
      const res = await api.post('/conventions', {
        classeId,
        stagePeriodeId: periode.id,
        startDate: periode.startDate?.split('T')[0],
        endDate: periode.endDate?.split('T')[0],
      });
      setPendingShares(s => ({ ...s, [periode.id]: res.data.id }));
      await fetchConventions();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la création de la convention');
    } finally {
      setSharingLoading(s => ({ ...s, [periode.id]: false }));
    }
  };

  const handleDownloadPdf = async (id) => {
    try {
      const res = await api.get(`/conventions/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `convention_${id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const blob = err.response?.data;
      if (blob instanceof Blob) {
        const text = await blob.text();
        try { alert(JSON.parse(text).error); } catch { alert('Erreur lors du téléchargement du PDF'); }
      } else {
        alert('Erreur lors du téléchargement du PDF');
      }
    }
  };

  const ConventionCard = ({ c }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <h3 className="font-semibold text-gray-800 truncate">{c.company?.name || <span className="text-gray-400 italic">Entreprise non renseignée</span>}</h3>
            <StatusBadge status={c.status} />
          </div>
          <p className="text-sm text-gray-500">
            {c.startDate ? fmt(c.startDate) : '?'} → {c.endDate ? fmt(c.endDate) : '?'}
            {c.totalDays ? ` · ${c.totalDays} jours` : ''}
          </p>
          {c.status === 'REJECTED' && c.rejectionComment && (
            <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-xs font-medium text-red-700 mb-1">Commentaire de rejet :</p>
              <p className="text-sm text-red-700">{c.rejectionComment}</p>
            </div>
          )}
          {c.status === 'DRAFT' && (
            <ShareConventionLink conventionId={c.id} />
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {['SIGNED', 'SIGNING', 'VALIDATED'].includes(c.status) && (
            <button onClick={() => handleDownloadPdf(c.id)}
              className="text-sm text-blue-600 hover:text-blue-800 border border-blue-200 hover:border-blue-400 px-3 py-1.5 rounded-lg transition-colors">
              PDF
            </button>
          )}
          {['DRAFT', 'REJECTED'].includes(c.status) && (
            <button onClick={() => navigate(`/student/convention/${c.id}`)}
              className="text-sm text-gray-700 hover:text-gray-900 border border-gray-200 hover:border-gray-400 px-3 py-1.5 rounded-lg transition-colors">
              Compléter
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) return <div className="text-gray-500 text-sm">Chargement...</div>;

  // Séparer conventions avec et sans période
  const withPeriode = conventions.filter(c => c.stagePeriodeId);
  const withoutPeriode = conventions.filter(c => !c.stagePeriodeId);

  // Grouper classes avec des périodes
  const classesWithPeriodes = classes.filter(c => c.periodes?.length > 0);
  const classesWithoutPeriodes = classes.filter(c => !c.periodes?.length);

  // Vérifier si on a des périodes et des conventions
  const hasPeriodes = classesWithPeriodes.length > 0;

  // Si aucune classe avec périodes et pas de conventions sans période → affichage simple legacy
  if (!hasPeriodes && withoutPeriode.length === 0) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Mes conventions de stage</h2>
          <button onClick={onNew} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            + Nouvelle convention
          </button>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <p className="text-gray-500 mb-4">Vous n'avez pas encore de convention.</p>
          <button onClick={onNew} className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2.5 rounded-lg transition-colors">
            Compléter ma convention
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-800 mb-6">Mes conventions de stage</h2>

      {/* Conventions par période */}
      {classesWithPeriodes.map(classe => (
        <div key={classe.id} className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            {classe.name}
            <span className="ml-2 text-xs font-normal text-gray-400">{classe.schoolYear?.label}</span>
          </h3>
          <div className="space-y-3">
            {classe.periodes.map(periode => {
              const conv = withPeriode.find(c => c.stagePeriodeId === periode.id);
              return (
                <div key={periode.id} className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2.5 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-sm text-gray-800">{periode.nom}</span>
                      <span className="text-xs text-gray-500">{fmt(periode.startDate)} → {fmt(periode.endDate)}</span>
                    </div>
                    {!conv && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate('/student/new', { state: { periodeId: periode.id, classeId: classe.id } })}
                          className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-colors"
                        >
                          Compléter ma convention
                        </button>
                        <button
                          onClick={() => handleShare(classe.id, periode)}
                          disabled={!!sharingLoading[periode.id]}
                          className="text-xs bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {sharingLoading[periode.id] ? '...' : 'Partager avec l\'entreprise'}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    {conv ? (
                      <ConventionCard c={conv} />
                    ) : pendingShares[periode.id] ? (
                      <ShareConventionLink conventionId={pendingShares[periode.id]} autoGenerate={true} />
                    ) : (
                      <p className="text-xs text-gray-400 py-1 px-1">Aucune convention pour cette période.</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Classes sans périodes — bouton nouvelle convention classique */}
      {classesWithoutPeriodes.length > 0 && !hasPeriodes && (
        <div className="mb-4 flex justify-end">
          <button onClick={onNew} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            + Nouvelle convention
          </button>
        </div>
      )}

      {/* Conventions sans période (legacy) */}
      {withoutPeriode.length > 0 && (
        <div className="mb-6">
          {hasPeriodes && (
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Autres conventions</h3>
          )}
          <div className="space-y-3">
            {withoutPeriode.map(c => <ConventionCard key={c.id} c={c} />)}
          </div>
          {!hasPeriodes && (
            <div className="mt-4 flex justify-end">
              <button onClick={onNew} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                + Nouvelle convention
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
